import { ReactNode, useEffect, useMemo, useState } from 'react';
import { AlertOctagonIcon, AlertTriangleIcon, ArrowDownIcon, ArrowUpIcon, InfoIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useBackendApi } from '@/hooks/fetch';
import { useOpenConfirmDialog } from '@/hooks/dialogs';
import { txToast } from '@/components/TxToaster';
import { cn } from '@/lib/utils';
import { Sparkline } from './Charts';
import { fmtAgo, fmtBytes, fmtDateTime } from './format';


// =============================================
//  Types partagés avec le core
// =============================================
export type MonitorStatus = {
    online: boolean;
    paused: boolean;
    collectEnabled: boolean;
    resourceCollecting?: boolean;
    collectingSince: number;
    lastReport: number | null;
    luaResources: number;
    reporting: number;
    notReporting: number;
    pending: number;
    notInstalled: number;
    hookOff: number;
    hookVersions: string[];
    clocks: string[];
    playerCount: number;
    hookLine: string;
};
export type MonitorAlert = {
    id: string;
    kind: string;
    severity: 'critical' | 'warning';
    subject: string;
    resource?: string;
    event?: string;
    player?: number;
    key?: string;
    message: string;
    value: number;
    threshold: number;
    firstSeen: number;
    lastSeen: number;
    count: number;
};
export type Severity = 'critical' | 'warning' | 'info' | 'ok';


// =============================================
//  Petits éléments
// =============================================
export function Segmented<T extends string>({ value, options, onChange }: {
    value: T;
    options: { value: T; label: string }[];
    onChange: (v: T) => void;
}) {
    return (
        <div className="inline-flex rounded-md border bg-muted p-0.5 text-xs">
            {options.map((o) => (
                <button
                    key={o.value}
                    type="button"
                    onClick={() => onChange(o.value)}
                    className={cn(
                        'rounded-sm px-2.5 py-1 font-medium text-muted-foreground hover:text-foreground',
                        value === o.value && 'bg-card text-foreground shadow-sm',
                    )}
                >
                    {o.label}
                </button>
            ))}
        </div>
    );
}

export function StatusLabel({ status }: { status: Severity | string }) {
    if (status === 'critical') {
        return (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#d03b3b]">
                <AlertOctagonIcon className="size-3.5" /> CRITICAL
            </span>
        );
    }
    if (status === 'warning') {
        return (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#b47c00] dark:text-[#fab219]">
                <AlertTriangleIcon className="size-3.5" /> WARNING
            </span>
        );
    }
    if (status === 'info') {
        return (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground">
                <InfoIcon className="size-3.5" /> INFO
            </span>
        );
    }
    return <span className="text-xs text-muted-foreground">OK</span>;
}

export function StatCard({ label, value, unit, sub, spark, tone }: {
    label: string;
    value: string;
    unit?: string;
    sub?: ReactNode;
    spark?: number[];
    tone?: Severity;
}) {
    return (
        <div className="flex min-w-0 flex-col rounded-lg border bg-card px-4 pb-2 pt-3">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="mt-1 flex items-baseline gap-1.5">
                <span className={cn(
                    'font-mono text-2xl font-semibold tabular-nums',
                    tone === 'critical' && 'text-[#d03b3b]',
                    tone === 'warning' && 'text-[#b47c00] dark:text-[#fab219]',
                )}>{value}</span>
                {unit && <span className="text-xs text-muted-foreground">{unit}</span>}
            </div>
            <div className="min-h-4 truncate text-xs text-muted-foreground">{sub}</div>
            {spark && <Sparkline values={spark} className="mt-1" />}
        </div>
    );
}


// =============================================
//  En-tête
// =============================================
export type ViewMode = 'live' | 'history';
export type LiveRange = '1m' | '5m' | '15m';
export type HistoryRange = '1' | '7' | '30';

export function MonitorHeader({ title, updatedAt, mode, setMode, range, setRange, histRange, setHistRange, livePaused, setLivePaused }: {
    title: string;
    updatedAt: number | null;
    mode: ViewMode;
    setMode: (m: ViewMode) => void;
    range: LiveRange;
    setRange: (r: LiveRange) => void;
    histRange: HistoryRange;
    setHistRange: (r: HistoryRange) => void;
    livePaused: boolean;
    setLivePaused: (p: boolean) => void;
}) {
    return (
        <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
            <div className="mr-auto">
                <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
                <p className="text-sm text-muted-foreground">
                    {mode === 'live'
                        ? <>Updated {fmtAgo(updatedAt)}{livePaused && ' · live view paused'}</>
                        : 'Aggregated history (5 min buckets)'}
                </p>
            </div>
            <Segmented<ViewMode>
                value={mode}
                onChange={setMode}
                options={[{ value: 'live', label: 'Live' }, { value: 'history', label: 'History' }]}
            />
            {mode === 'live' ? (
                <>
                    <Segmented<LiveRange>
                        value={range}
                        onChange={setRange}
                        options={[{ value: '1m', label: '1m' }, { value: '5m', label: '5m' }, { value: '15m', label: '15m' }]}
                    />
                    <Button size="xs" variant="outline-muted" onClick={() => setLivePaused(!livePaused)}>
                        {livePaused ? 'Resume live' : 'Pause live'}
                    </Button>
                </>
            ) : (
                <Segmented<HistoryRange>
                    value={histRange}
                    onChange={setHistRange}
                    options={[{ value: '1', label: '24h' }, { value: '7', label: '7d' }, { value: '30', label: '30d' }]}
                />
            )}
        </div>
    );
}


// =============================================
//  Bandeau de statut + actions
// =============================================
export function StatusBanner({ feature, status, historySize, extra, onChanged }: {
    feature: 'events' | 'statebags';
    status?: MonitorStatus;
    historySize?: number;
    extra?: ReactNode;
    onChanged: () => void;
}) {
    const openConfirm = useOpenConfirmDialog();
    const [hookMode, setHookMode] = useState<'install' | 'uninstall' | null>(null);
    const pauseApi = useBackendApi({ method: 'POST', path: `/vanta/${feature}/pause` });
    const resetApi = useBackendApi({ method: 'POST', path: `/vanta/${feature}/reset` });
    const collectApi = useBackendApi({ method: 'POST', path: `/vanta/${feature}/collection` });

    const call = (api: typeof pauseApi, data: object, okMsg: string) => {
        api({
            data,
            toastLoadingMessage: 'Saving…',
            genericHandler: { successMsg: okMsg },
            success: () => onChanged(),
        });
    };

    const s = status;
    const items: ReactNode[] = [];
    if (s) {
        items.push(
            <span key="since">Collecting since <b className="font-medium text-foreground">{fmtDateTime(s.collectingSince)}</b></span>,
            <span key="last">Last report <b className={cn('font-medium', s.online ? 'text-foreground' : 'text-[#d03b3b]')}>{fmtAgo(s.lastReport)}</b></span>,
            <span key="lua"><b className="font-medium text-foreground">{s.reporting}</b> Lua reporting · <b className={cn('font-medium', s.notReporting ? 'text-[#b47c00] dark:text-[#fab219]' : 'text-foreground')}>{s.notReporting}</b> not reporting</span>,
            <span key="hook">Hook {s.hookVersions.length ? s.hookVersions.join(', ') : 'not loaded'}{s.clocks.length ? ` · clock ${s.clocks.join('/')}` : ''}</span>,
            <span key="hist">History 30d · {fmtBytes(historySize ?? 0)}</span>,
        );
        if (s.pending) items.push(<span key="pending">{s.pending} pending restart</span>);
        if (s.paused) items.push(<span key="paused" className="font-medium text-[#b47c00] dark:text-[#fab219]">Recording paused</span>);
        if (!s.collectEnabled) items.push(<span key="off" className="font-medium text-[#d03b3b]">Collection disabled</span>);
    }

    return (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border bg-card px-4 py-2.5 text-xs text-muted-foreground">
            {s ? items.map((it, i) => (
                <span key={i} className="flex items-center gap-4">
                    {i > 0 && <span className="h-3 w-px bg-border" />}
                    {it}
                </span>
            )) : <span>Loading status…</span>}
            {extra}
            <div className="ml-auto flex flex-wrap gap-1.5">
                <Button size="xs" variant="outline-muted" disabled={!s}
                    onClick={() => call(pauseApi, { paused: !s?.paused }, s?.paused ? 'Recording resumed.' : 'Recording paused.')}>
                    {s?.paused ? 'Resume' : 'Pause'}
                </Button>
                <Button size="xs" variant="outline-muted" disabled={!s}
                    onClick={() => openConfirm({
                        title: 'Reset live data',
                        message: 'Clears the last 15 minutes of live data and all alerts. The 30-day history is kept.',
                        actionLabel: 'Reset',
                        onConfirm: () => call(resetApi, {}, 'Live data cleared.'),
                    })}>
                    Reset
                </Button>
                <Button size="xs" variant={s?.collectEnabled === false ? 'outline-success' : 'outline-warning'} disabled={!s}
                    onClick={() => call(collectApi, { enabled: !s?.collectEnabled },
                        s?.collectEnabled ? 'Collection disabled (applied within 5s).' : 'Collection enabled.')}>
                    {s?.collectEnabled === false ? 'Enable collection' : 'Disable collection'}
                </Button>
                <Button size="xs" variant="outline-muted" onClick={() => setHookMode('install')}>Install hook</Button>
                <Button size="xs" variant="outline-destructive" onClick={() => setHookMode('uninstall')}>Uninstall</Button>
            </div>
            <HookDialog feature={feature} mode={hookMode} onClose={() => setHookMode(null)} />
        </div>
    );
}


// =============================================
//  Installation / désinstallation du hook
// =============================================
type HookPlanItem = {
    resource: string;
    path: string;
    manifest: string;
    action: 'install' | 'remove' | 'already' | 'absent' | 'skip';
    reason?: string;
    escrowed?: boolean;
    done?: boolean;
    error?: string;
};
type HookRunResult = {
    dryRun: boolean;
    mode: 'install' | 'uninstall';
    resourcesRoot: string;
    backupDir: string | null;
    items: HookPlanItem[];
    counts: Record<string, number>;
    error?: string;
};

function HookDialog({ feature, mode, onClose }: {
    feature: 'events' | 'statebags';
    mode: 'install' | 'uninstall' | null;
    onClose: () => void;
}) {
    const [plan, setPlan] = useState<HookRunResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [includeEscrowed, setIncludeEscrowed] = useState(false);
    const [filter, setFilter] = useState('');
    const api = useBackendApi<HookRunResult>({ method: 'POST', path: `/vanta/${feature}/hook-${mode ?? 'install'}` });
    const changeAction = mode === 'install' ? 'install' : 'remove';

    const run = (dryRun: boolean, escrow = includeEscrowed) => {
        setLoading(true);
        api({
            data: { dryRun, includeEscrowed: escrow },
            timeout: 30_000,
            success: (data) => {
                if ('error' in data && data.error) {
                    txToast.error(data.error);
                } else {
                    setPlan(data);
                    if (!dryRun) {
                        const done = data.items.filter((i) => i.done).length;
                        txToast.success(`${done} manifest(s) modified. Restart those resources (or the server) to apply.`);
                    }
                }
            },
            error: (msg) => txToast.error(msg),
            finally: () => setLoading(false),
        });
    };

    const onOpenChange = (open: boolean) => {
        if (open) return;
        setPlan(null);
        setFilter('');
        onClose();
    };

    // dry run automatique à l'ouverture
    useEffect(() => {
        if (mode) run(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    const rows = useMemo(() => {
        if (!plan) return [];
        const f = filter.toLowerCase();
        return plan.items
            .filter((i) => !f || i.resource.toLowerCase().includes(f) || i.path.toLowerCase().includes(f))
            .sort((a, b) => Number(b.action === changeAction) - Number(a.action === changeAction) || a.resource.localeCompare(b.resource));
    }, [plan, filter, changeAction]);
    const toChange = plan?.items.filter((i) => i.action === changeAction).length ?? 0;

    return (
        <Dialog open={!!mode} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl">
                <DialogHeader>
                    <DialogTitle>{mode === 'install' ? 'Install the Vanta hook' : 'Uninstall the Vanta hook'}</DialogTitle>
                    <DialogDescription>
                        {mode === 'install'
                            ? <>Adds <code className="text-xs">shared_script '@monitor/resource/vanta_hook.lua' -- vanta-hook</code> before the other scripts of every server-side Lua resource. Each modified manifest is backed up in the txAdmin profile (data/vanta/hookBackups).</>
                            : <>Removes the <code className="text-xs">-- vanta-hook</code> line from every manifest. Each modified manifest is backed up first.</>}
                        {' '}Changes apply when the resources are restarted.
                    </DialogDescription>
                </DialogHeader>
                {plan?.dryRun === false && (
                    <div className="rounded-md border px-3 py-2 text-sm">
                        Done: {plan.items.filter((i) => i.done).length} manifest(s) modified
                        {plan.items.some((i) => i.error) && <span className="text-[#d03b3b]"> · {plan.items.filter((i) => i.error).length} error(s)</span>}
                        {plan.backupDir && <div className="mt-1 break-all text-xs text-muted-foreground">Backup: {plan.backupDir}</div>}
                    </div>
                )}
                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    {plan ? (
                        <span>
                            {plan.dryRun ? 'Dry run' : 'Result'} · {plan.resourcesRoot} ·{' '}
                            {Object.entries(plan.counts).map(([k, v]) => `${v} ${k}`).join(' · ')}
                        </span>
                    ) : <span>{loading ? 'Scanning resources…' : ''}</span>}
                    <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter resources"
                        className="ml-auto h-7 w-48 text-xs" />
                </div>
                {mode === 'install' && (
                    <label className="flex items-center gap-2 text-xs">
                        <Checkbox checked={includeEscrowed} onCheckedChange={(v) => {
                            setIncludeEscrowed(v === true);
                            run(true, v === true);
                        }} />
                        Include escrowed resources (.fxap). Their manifest stays editable, but test on a dev server first.
                    </label>
                )}
                <div className="max-h-[50vh] overflow-auto rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="h-8 text-xs">Action</TableHead>
                                <TableHead className="h-8 text-xs">Resource</TableHead>
                                <TableHead className="h-8 text-xs">Path</TableHead>
                                <TableHead className="h-8 text-xs">Note</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((i) => (
                                <TableRow key={i.path}>
                                    <TableCell className={cn('py-1 text-xs font-medium', i.action === changeAction ? 'text-foreground' : 'text-muted-foreground')}>
                                        {i.done ? (i.action === 'install' ? 'installed' : 'removed') : i.action}
                                    </TableCell>
                                    <TableCell className="py-1 font-mono text-xs">{i.resource}</TableCell>
                                    <TableCell className="py-1 text-xs text-muted-foreground">{i.path}/{i.manifest}</TableCell>
                                    <TableCell className="py-1 text-xs text-muted-foreground">{i.error ?? i.reason ?? ''}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
                <DialogFooter>
                    <Button size="sm" variant="outline-muted" onClick={() => onOpenChange(false)}>Close</Button>
                    <Button size="sm" variant={mode === 'install' ? 'default' : 'destructive'}
                        disabled={loading || !plan || !plan.dryRun || toChange === 0}
                        onClick={() => run(false)}>
                        {mode === 'install' ? `Install in ${toChange} resource(s)` : `Remove from ${toChange} resource(s)`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


// =============================================
//  Tableau triable
// =============================================
export type Column<T> = {
    key: string;
    label: string;
    value?: (row: T) => number | string;
    render?: (row: T) => ReactNode;
    align?: 'left' | 'right';
    className?: string;
    title?: string;
};

export function DataTable<T>({ rows, columns, rowKey, defaultSort, defaultDesc = true, empty = 'No data yet.', pageSize = 100, onRowClick }: {
    rows: T[];
    columns: Column<T>[];
    rowKey: (row: T) => string;
    defaultSort?: string;
    defaultDesc?: boolean;
    empty?: string;
    pageSize?: number;
    onRowClick?: (row: T) => void;
}) {
    const [sortKey, setSortKey] = useState<string | undefined>(defaultSort);
    const [desc, setDesc] = useState(defaultDesc);
    const [limit, setLimit] = useState(pageSize);
    const sorted = useMemo(() => {
        const col = columns.find((c) => c.key === sortKey);
        if (!col) return rows;
        const get = col.value ?? ((r: T) => (r as any)[col.key]);
        return [...rows].sort((a, b) => {
            const va = get(a);
            const vb = get(b);
            const cmp = typeof va === 'number' && typeof vb === 'number'
                ? va - vb
                : String(va ?? '').localeCompare(String(vb ?? ''));
            return desc ? -cmp : cmp;
        });
    }, [rows, columns, sortKey, desc]);

    const onSort = (key: string) => {
        if (key === sortKey) setDesc(!desc);
        else {
            setSortKey(key);
            setDesc(true);
        }
    };

    return (
        <div className="rounded-lg border bg-card">
            <Table>
                <TableHeader>
                    <TableRow>
                        {columns.map((c) => (
                            <TableHead
                                key={c.key}
                                title={c.title}
                                onClick={() => onSort(c.key)}
                                className={cn(
                                    'h-9 cursor-pointer select-none whitespace-nowrap text-[11px] font-semibold uppercase tracking-wider hover:text-foreground',
                                    c.align === 'right' && 'text-right',
                                )}
                            >
                                <span className="inline-flex items-center gap-1">
                                    {c.label}
                                    {sortKey === c.key && (desc ? <ArrowDownIcon className="size-3" /> : <ArrowUpIcon className="size-3" />)}
                                </span>
                            </TableHead>
                        ))}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {sorted.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={columns.length} className="py-8 text-center text-sm text-muted-foreground">{empty}</TableCell>
                        </TableRow>
                    )}
                    {sorted.slice(0, limit).map((row) => (
                        <TableRow key={rowKey(row)} onClick={onRowClick ? () => onRowClick(row) : undefined}
                            className={cn(onRowClick && 'cursor-pointer')}>
                            {columns.map((c) => (
                                <TableCell
                                    key={c.key}
                                    className={cn('py-1.5 text-xs', c.align === 'right' && 'text-right font-mono tabular-nums', c.className)}
                                >
                                    {c.render ? c.render(row) : String((row as any)[c.key] ?? '')}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            {sorted.length > limit && (
                <div className="border-t px-4 py-2 text-center">
                    <Button size="xs" variant="ghost-muted" onClick={() => setLimit(limit + pageSize)}>
                        Show more ({sorted.length - limit} remaining)
                    </Button>
                </div>
            )}
        </div>
    );
}

export function AlertsTable({ alerts }: { alerts: MonitorAlert[] }) {
    return (
        <DataTable<MonitorAlert>
            rows={alerts}
            rowKey={(a) => a.id}
            defaultSort="lastSeen"
            empty="No alert. Thresholds are checked on every 5 s report."
            columns={[
                { key: 'severity', label: 'Status', value: (a) => (a.severity === 'critical' ? 2 : 1), render: (a) => <StatusLabel status={a.severity} /> },
                { key: 'message', label: 'Alert', render: (a) => <span className="text-foreground">{a.message}</span> },
                { key: 'kind', label: 'Type', render: (a) => <span className="font-mono text-muted-foreground">{a.kind}</span> },
                { key: 'resource', label: 'Resource', render: (a) => <span className="font-mono">{a.resource ?? '–'}</span> },
                { key: 'count', label: 'Hits', align: 'right' },
                { key: 'firstSeen', label: 'First seen', render: (a) => fmtDateTime(a.firstSeen) },
                { key: 'lastSeen', label: 'Last seen', render: (a) => fmtAgo(a.lastSeen) },
            ]}
        />
    );
}

export function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
    return (
        <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="h-9 max-w-md" />
    );
}
